import useHelpDocuments, {
    Category,
    IDocument,
} from 'hooks/useHelpDocument/useHelpDocument'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'

interface IHelpDocumentContext {
    selectCategory: Category
    allDocuments: IDocument[]
    docIndex: lunr.Index | undefined
    loadingContent: boolean
    categoryDocuments: IDocument[]
    currentDocument?: IDocument
    onSelectCategory: (val: Category) => void
    onSelectCurrentDocument: (document: IDocument) => void
}

const HelpDocumentContext = createContext<IHelpDocumentContext>({
    selectCategory: 'General',
    allDocuments: [],
    docIndex: undefined,
    loadingContent: false,
    categoryDocuments: [],
    currentDocument: undefined,
    onSelectCategory: function (category: Category): void {
        throw new Error('Function not implemented.')
    },
    onSelectCurrentDocument: function (document: IDocument): void {
        throw new Error('Function not implemented.')
    },
})

export const defaultTitleDocument = new Map<Category, string>([
    ['General', 'Configuring Payment Methods in Restaurant Management System'],
    ['quick service', 'Close of Day Procedures'],
    ['table service', 'Join Tables'],
    ['kds', 'Send orders to the Kitchen with KDS'],
    ['help', ''],
])

function HelpDocumentContextProvider({ children }: { children: ReactNode }) {
    const [selectCategory, setSelectCategory] = useState<Category>('General')
    const { docs: allDocuments, docIndex, loadingContent } = useHelpDocuments()
    const [currentDocument, setCurrentDocument] = useState<IDocument>()

    const reSelectCurrentDocument = useCallback(
        (category: Category) => {
            const firstDocument = allDocuments.find(
                (i) => i.title === defaultTitleDocument.get(category),
            )
            setCurrentDocument(firstDocument)
        },
        [allDocuments],
    )

    const onSelectCategory = useCallback(
        (value: Category) => {
            setSelectCategory(value)
            reSelectCurrentDocument(value)
        },
        [reSelectCurrentDocument],
    )

    const onSelectCurrentDocument = useCallback((doc: IDocument) => {
        setCurrentDocument(doc)
        setSelectCategory(doc.category)
    }, [])

    const categoryDocuments = useMemo(
        () => allDocuments.filter((i) => i.category === selectCategory),
        [selectCategory, allDocuments],
    )

    return (
        <HelpDocumentContext.Provider
            value={{
                selectCategory,
                onSelectCategory,
                allDocuments,
                docIndex,
                loadingContent,
                categoryDocuments,
                currentDocument,
                onSelectCurrentDocument,
            }}
        >
            {children}
        </HelpDocumentContext.Provider>
    )
}

export default HelpDocumentContextProvider

export const useHelpDocumentContext = () => {
    const context = useContext(HelpDocumentContext)
    return context
}
